import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { toApiError } from '../utils/api-error';

export const AUDIENCE_TYPES = {
  INTERESTS: 'interests',
  CUSTOM: 'custom',
  CUSTOM_QUERY: 'customquery',
} as const;

export type AudienceType = typeof AUDIENCE_TYPES[keyof typeof AUDIENCE_TYPES];

export interface AudienceApiResponse {
  ID: string;
  Name: string;
  Description?: string;
  Status?: string;
  IsRunning?: boolean | number;
  CreatedByUserID?: string;
  CreatedByUserName?: string;
  CreatedByName?: string;
  CreatedBy?: string;
  CreatedByUser?: {
    ID?: string;
    Name?: string;
  };
  CreationTime?: string;
  Schedulation?: string;
  NextSchedulationDate?: string;
  EndDate?: string;
  LastExtraction?: string;
  Volumes?: number | string;
  ActivationItems?: ActivationItemApi[];
  FilterItems?: AudienceFilterItem[] | string;
  QueryComposition?: string;
  ExpandSourceGroupID?: string | null;
  AudienceQuality?: string | number | null;
  AudienceQualityIndex?: number | string | null;
  ExtendVolumes?: number | string | null;
  GlobalTimerange?: number | string | null;
  Expanded?: number | string | null;
  Tags?: string[] | string | null;
  EntityType?: string | null;
  EntityID?: string | null;
  RulePreview?: string | null;
  rulePreview?: string | null;
  FrontendPreview?: string | AudienceFrontendPreview | null;
  ExpandSimilarUsers?: boolean | null;
  SourceGroupIDs?: string[] | null;
  IsPublic?: number | boolean;
  PreviewAccuracy?: AudiencePreviewAccuracyApi | null;
  AccuracyPreview?: AudiencePreviewAccuracyApi | null;
}

export interface ActivationItemApi {
  ID: string;
  DestinationID?: string;
  EntityID?: string;
  CreationTime?: string;
  LastUpdate?: string;
  NextExecutionDate?: string;
  LastPushTime?: string;
  Status?: string;
  Volume?: number;
  ExternalSegmentID?: string;
  ChannelData?: Record<string, unknown> | string | null;
  ExcludeControlGroup?: boolean;
}

export interface AudienceFilterItem {
  ID: number;
  filterType: string;
  filterValue: any;
  entity?: {
    EntityID: string;
    EntityType: string;
  };
  timerange?: number;
}

export interface AudienceCreatePayload {
  Name: string;
  Status?: string;
  IsPublic: boolean;
  Description?: string;
  Brief?: string;
  NextSchedulationDate: string;
  EndDate: string;
  Schedulation: string;
  EntityID: string;
  FilterItems: AudienceFilterItem[];
  QueryComposition: string;
  GlobalTimerange: number;
  ExpandSourceGroupID: string;
  AudienceQuality?: string | number | null;
  ExtendVolumes?: number | null;
  Expanded?: number | null;
  Tags?: string[] | string;
  CustomGroupFlag?: boolean;
  SourcesDCR?: string[];
  SourceGroupIDs?: string[];
  ExpandSimilarUsers?: boolean | null;
  FrontendPreview?: { AudienceType: string; RulePreview?: string | null; ExpandVersion?: string | null } | null;
  PreviewOnly?: boolean;
  PreviewAccuracyStep?: number | null;
  AudienceQualityIndex?: number | null;
  // Forza il reset dell'indice di qualità e l'eliminazione della curva salvata lato backend:
  // usato quando i filtri che determinano la curva sono cambiati rispetto al valore salvato.
  ResetQualityIndex?: boolean;
}

export interface AudienceUpdatePayload extends AudienceCreatePayload {
  ID: string;
}

export interface AudiencePreviewPointApi {
  step?: number | string | null;
  users?: number | string | null;
  cumulativeUsers?: number | string | null;
  minDistance?: number | string | null;
  maxDistance?: number | string | null;
  label?: string | null;
}

export interface AudiencePreviewAccuracyApi {
  Points?: AudiencePreviewPointApi[] | number[] | string | null;
  InflectionPoint?: number | string | null;
  InflectionVolume?: number | string | null;
  SuggestedPoint?: number | string | null;
  SuggestedVolume?: number | string | null;
  points?: AudiencePreviewPointApi[] | number[] | null;
  buckets?: AudiencePreviewPointApi[] | number[] | null;
  cumulativeUsers?: number[] | null;
  defaultStep?: number | string | null;
  selectedStep?: number | string | null;
}

export interface AudienceCurveVolumePointApi {
  Distance?: number | string | null;
  TNCIDs?: number | string | null;
}

export interface AudienceCurveVolumeDataApi {
  AudienceID?: string | null;
  Points?: AudienceCurveVolumePointApi[] | null;
  InflectionPoint?: number | string | null;
  InflectionVolume?: number | string | null;
  SuggestedPoint?: number | string | null;
  SuggestedVolume?: number | string | null;
}

/**
 * One day of the audience-volumes trend chart. `Volumes` is the total for that day; the optional
 * breakdown fields are present only on the per-audience endpoint (`/audience/volumes`), not on the
 * global executions one (`/audience/executions`), which returns just Date + Volumes.
 * NB: the backend returns `RemovedUsers` already negated (0 - RemovedUsers).
 */
export interface AudienceVolumeTrendPoint {
  Date: string;
  Volumes: number | string;
  NewUsers?: number | string | null;
  ExistingUsers?: number | string | null;
  RemovedUsers?: number | string | null;
  FutureUsers?: number | string | null;
}

/** One sociodemo bucket: gender (M/F/Generic) × age range, with its volume. */
export interface AudienceSociodemoRow {
  Gender: string;
  AgesRange: string;
  AudienceGenderVolumes: number | string;
}

/** Target-info for the Tags tab: the audience's tags + its target size. */
export interface AudienceTargetInfo {
  Tags: string[];
  TargetSize: number | string;
}

/** BigQuery cost estimation constants (same ones the admin-dashboard uses to price extractions). */
export const BIGQURY_COST_PER_TiB = 5.86;
export const MIN_EXECUTION_TiB = 0.033;

/** Raw cost-insights payload for the advanced Details tab; costs are computed client-side from the billed bytes. */
export interface AudienceInsightsCosts {
  LastUpdateDate: string;
  AudienceID: string;
  TotalBytesBilled: number;
  AverageBytesBilled: number;
  LastExtractionBytesBilled?: number | null;
  AvarageExecutionCost?: number;
  LastExtractionCost?: number;
  TotalCost?: number;
  NumberOfExtractions: number;
}

/** One changelog entry for an audience (the backend already resolves UserName). */
export interface AudienceChangelogEvent {
  UserID: string;
  UserName: string;
  UpdateDatetime: string;
  Description: string;
}

/** Latest volumes for a single audience broken down by Export ID Type (per-audience Volumes tab). */
export interface AudienceExportVolume {
  AudienceID?: string;
  ExportIDType: string;
  UpdateDate?: string;
  Volumes: number | string;
  ExportIDName?: string | null;
  EntityID?: string | null;
  EntityName?: string | null;
}

export interface AudienceFrontendPreview {
  AudienceType?: string | null;
  ExpandVersion?: string | null;
  RulePreview?: string | null;
  AccuracyPreview?: AudiencePreviewAccuracyApi | null;
  PreviewAccuracy?: AudiencePreviewAccuracyApi | null;
}

export interface AudienceIntersection {
  sourceAudienceId: string;
  targetAudienceId: string;
  tncIds: number;
  sourceOverlap: number;
  targetOverlap: number;
  sourceVolumes: number;
  targetVolumes: number;
  targetAudienceName: string;
  sourceAudienceName: string;
  targetAudienceDescription?: string;
  // Target owner/visibility, returned by the backend so the overlap modal no
  // longer has to cross-reference the page's audience list.
  targetEntityId?: string | null;
  targetIsPublic?: boolean | number | null;
  sourceEntityId?: string | null;
  targetEntityType?: string | null;
  // Frontend-only: owner display label, resolved from targetEntityId.
  targetOwnerLabel?: string;
}

export interface CountryOption {
  code: string;
  name: string;
}

export interface GoalOption {
  ID: string;
  Description: string;
  Status?: number | boolean;
  SourceGroupID?: string | null;
}

interface ApiResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AudienceApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getAudiences(params?: { status?: string; audienceId?: string }): Observable<AudienceApiResponse[]> {
    let httpParams = new HttpParams();
    if (params?.status) {
      httpParams = httpParams.set('Status', params.status);
    }
    if (params?.audienceId) {
      httpParams = httpParams.set('AudienceID', params.audienceId);
    }

    const endpoint = this.composeEndpoint('/webapp/audience/crud/list');

    return this.http.get<ApiResponse<AudienceApiResponse[]>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params: httpParams,
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load audiences');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load audiences', error);
        return throwError(() => error);
      })
    );
  }

  getGoals(status: 'active' | 'inactive' | 'all' = 'active'): Observable<GoalOption[]> {
    let httpParams = new HttpParams();
    if (status && status !== 'all') {
      httpParams = httpParams.set('Status', status);
    }
    const endpoint = this.composeEndpoint('/webapp/crud/goals');
    return this.http.get<ApiResponse<GoalOption[]>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params: httpParams,
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load goals');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load goals', error);
        return throwError(() => error);
      })
    );
  }

  createAudience(payload: AudienceCreatePayload): Observable<AudienceApiResponse> {
    const endpoint = this.composeEndpoint('/webapp/audience/crud/new');

    return this.http.post<ApiResponse<AudienceApiResponse>>(
      endpoint.url,
      payload,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          console.error('[audience-api] Backend error response:', response.error);
          throw new Error(response.error.message || 'Unable to create audience');
        }
        if (!response.payload) {
          throw new Error('Missing payload from create audience response');
        }
        return response.payload;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to create audience', error);
        console.error('[audience-api] Error details:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error
        });
        return throwError(() => error);
      })
    );
  }

  previewAudience(payload: AudienceCreatePayload | AudienceUpdatePayload): Observable<AudienceApiResponse> {
    const endpoint = this.composeEndpoint('/webapp/audience/crud/preview');

    return this.http.post<ApiResponse<AudienceApiResponse>>(
      endpoint.url,
      payload,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to preview audience');
        }
        if (!response.payload) {
          throw new Error('Missing payload from preview audience response');
        }
        return response.payload;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to preview audience', error);
        return throwError(() => error);
      })
    );
  }

  getCurveVolumeByDistance(audienceId: string, variant?: 'standard' | 'weighted' | null): Observable<AudienceCurveVolumeDataApi | null> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/curve-volume-by-distance');
    let params = new HttpParams().set('AudienceID', audienceId);
    if (variant) {
      // Override della tabella (anteprima live standard/pesata); se assente il backend usa il valore salvato.
      params = params.set('Variant', variant);
    }

    return this.http.get<ApiResponse<AudienceCurveVolumeDataApi>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params,
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load audience curve');
        }
        return response.payload ?? null;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load audience curve', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Global extractions trend (Console/advanced): audiences extracted per day over the last ~31 days
   * plus scheduled extractions for the next ~31 days. Entity-scoped server-side. Returns just
   * Date + Volumes (count of audiences), so future days render as the "future" (faded) series.
   */
  getAudienceExecutions(): Observable<AudienceVolumeTrendPoint[]> {
    const endpoint = this.composeEndpoint('/webapp/audience/executions');
    return this.http.get<ApiResponse<AudienceVolumeTrendPoint[]>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load audience executions');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load audience executions', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Per-audience daily volumes trend (last ~14 days) for the advanced Volumes panel: Volumes with the
   * New/Removed breakdown (RemovedUsers arrives negated). NB param casing is `AudienceId` here.
   */
  getAudienceVolumeTrend(audienceId: string): Observable<AudienceVolumeTrendPoint[]> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/volumes');
    const params = new HttpParams().set('AudienceId', audienceId);
    return this.http.get<ApiResponse<AudienceVolumeTrendPoint[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load audience volumes');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load audience volumes trend', error);
        return throwError(() => error);
      })
    );
  }

  /** Latest per-Export-ID-Type volumes for a single audience (advanced Volumes panel). */
  getAudienceExportVolumes(audienceId: string): Observable<AudienceExportVolume[]> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/crud/exports');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.get<ApiResponse<AudienceExportVolume[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load audience export volumes');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load audience export volumes', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Per-activation daily push-volumes trend (last ~14 days) for the tiny advanced push chart on each
   * destination card: Volumes with the New/Removed breakdown (RemovedUsers negated). Param: `ActivationId`.
   */
  getActivationVolumesTrend(activationId: string): Observable<AudienceVolumeTrendPoint[]> {
    if (!activationId) {
      return throwError(() => new Error('Missing activation identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/activation/volumes');
    const params = new HttpParams().set('ActivationId', activationId);
    return this.http.get<ApiResponse<AudienceVolumeTrendPoint[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load activation volumes');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load activation volumes trend', error);
        return throwError(() => error);
      })
    );
  }

  /** Sociodemo breakdown (gender × age) for the advanced Sociodemo tab. */
  getAudienceSociodemo(audienceId: string): Observable<AudienceSociodemoRow[]> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience-analysis/sociodemo');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.get<ApiResponse<AudienceSociodemoRow[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load sociodemo');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load sociodemo', error);
        return throwError(() => error);
      })
    );
  }

  /** Target-info (tags + target size) for the advanced Tags tab. */
  getAudienceTargetInfo(audienceId: string): Observable<AudienceTargetInfo | null> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience-analysis/target-info');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.get<ApiResponse<AudienceTargetInfo>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load target info');
        }
        return response.payload ?? null;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load target info', error);
        return throwError(() => error);
      })
    );
  }

  /** BigQuery cost insights for the advanced Details tab (loaded on demand — button-triggered, not auto-fetched). */
  getAudienceInsightsCosts(audienceId: string): Observable<AudienceInsightsCosts | null> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience-analysis/costs');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.get<ApiResponse<AudienceInsightsCosts>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load cost insights');
        }
        const data = response.payload ?? null;
        if (data) {
          const totalTiB = data.TotalBytesBilled ? data.TotalBytesBilled / Math.pow(1024, 4) : 0;
          data.TotalCost = Math.round((totalTiB + MIN_EXECUTION_TiB * data.NumberOfExtractions) * BIGQURY_COST_PER_TiB * 100) / 100;
          const avgTiB = data.AverageBytesBilled ? data.AverageBytesBilled / Math.pow(1024, 4) : 0;
          data.AvarageExecutionCost = Math.round((avgTiB + MIN_EXECUTION_TiB) * BIGQURY_COST_PER_TiB * 100) / 100;
          if (data.LastExtractionBytesBilled != null) {
            const lastTiB = Number(data.LastExtractionBytesBilled) / Math.pow(1024, 4);
            data.LastExtractionCost = Math.round((lastTiB + MIN_EXECUTION_TiB) * BIGQURY_COST_PER_TiB * 100) / 100;
          }
        }
        return data;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load cost insights', error);
        return throwError(() => error);
      })
    );
  }

  /** Changelog events for an audience (advanced Changelog tab). */
  getAudienceChangelog(audienceId: string): Observable<AudienceChangelogEvent[]> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/changelog');
    const params = new HttpParams().set('ObjectID', audienceId).set('ObjectType', 'audience');
    return this.http.get<ApiResponse<AudienceChangelogEvent[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote), params, withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load changelog');
        }
        return response.payload ?? [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load changelog', error);
        return throwError(() => error);
      })
    );
  }

  updateAudience(payload: AudienceUpdatePayload): Observable<AudienceApiResponse> {
    if (!payload?.ID) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/crud/update');
    return this.http.post<ApiResponse<AudienceApiResponse>>(
      endpoint.url,
      payload,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        // Identifica al backend che la richiesta arriva dal form di edit di Engine (non da uno
        // strumento di editing manuale della SQL): se la SQLQuery salvata diverge da quella che i
        // filtri attuali genererebbero, il backend deve rigenerarla invece di congelare l'audience
        // come Custom Query.
        params: { Source: 'engine-ui' },
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to update audience');
        }
        if (!response.payload) {
          throw new Error('Missing payload from update audience response');
        }
        return response.payload;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to update audience', error);
        return throwError(() => error);
      })
    );
  }

  extractTags(description: string): Observable<string[]> {
    if (!description || !description.trim().length) {
      return throwError(() => new Error('Missing audience description'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/extract-tags');
    const body = { AudienceDescription: description };
    return this.http.post<ApiResponse<{ tags: string[] }>>(
      endpoint.url,
      body,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to extract tags');
        }
        const tags = response.payload?.tags;
        return Array.isArray(tags) ? tags : [];
      }),
      catchError(error => {
        console.error('[audience-api] Failed to extract tags', error);
        return throwError(() => error);
      })
    );
  }

  updateAudienceStatus(audienceId: string, status: 'active' | 'stopped', endDate: string | null): Observable<void> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/crud/update/status');
    const body = {
      AudienceID: audienceId,
      Status: status,
      EndDate: endDate
    };
    return this.http.post<ApiResponse<string>>(
      endpoint.url,
      body,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        this.ensureOkResponse(response, 'Unable to update audience status');
      }),
      catchError(error => {
        console.error('[audience-api] Failed to update status', error);
        return throwError(() => error);
      })
    );
  }

  deleteAudience(audienceId: string): Observable<void> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/crud/delete');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.delete<ApiResponse<string>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params,
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        this.ensureOkResponse(response, 'Unable to delete audience');
      }),
      catchError(error => {
        console.error('[audience-api] Failed to delete audience', error);
        return throwError(() => error);
      })
    );
  }

  reportAudienceError(audienceId: string): Observable<void> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/crud/report-error');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.post<ApiResponse<string>>(
      endpoint.url,
      null,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params,
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        this.ensureOkResponse(response, 'Unable to send error report');
      }),
      catchError(error => {
        console.error('[audience-api] Failed to report audience error', error);
        return throwError(() => error);
      })
    );
  }

  extractAudience(audienceId: string): Observable<string | null> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/scheduled/audience/extract');
    const params = new HttpParams().set('AudienceID', audienceId);
    return this.http.get<ApiResponse<string>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params,
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to start export');
        }
        return response.payload ?? null;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to extract audience', error);
        // Il rifiuto può avere un motivo che l'utente deve leggere (es. entity sandbox, HTTP 409):
        // si propaga il messaggio dell'API invece della stringa generata da Angular.
        return throwError(() => toApiError(error));
      })
    );
  }

  getAudienceIntersections(audienceId: string, targetAudienceId?: string): Observable<AudienceIntersection[]> {
    if (!audienceId) {
      return throwError(() => new Error('Missing audience identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/crud/get-intersections');
    let params = new HttpParams().set('AudienceID', audienceId);
    if (targetAudienceId) {
      params = params.set('TargetAudienceID', targetAudienceId);
    }
    return this.http.get<ApiResponse<any[]>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        params,
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load intersections');
        }
        const payload = response.payload ?? [];
        return payload.map(item => this.mapIntersection(item));
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load intersections', error);
        return throwError(() => error);
      })
    );
  }

  requestActivation(payload: {
    AudienceID: string;
    DestinationTypeID: number;
    EntityID?: string;
    ExternalSegmentID?: string;
    ChannelData?: Record<string, unknown>;
    ExportIDType?: string[];
  }): Observable<void> {
    const endpoint = this.composeEndpoint('/webapp/audience/activations/request-activation');
    return this.http.post<ApiResponse<void>>(
      endpoint.url,
      payload,
      { headers: this.buildHeaders(endpoint.useRemote), withCredentials: false }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to request activation');
        }
      }),
      catchError(error => {
        console.error('[audience-api] Failed to request activation', error);
        return throwError(() => error);
      })
    );
  }

  addActivationItem(payload: { AudienceID: string; DestinationID: number; ChannelData: Record<string, unknown>; EntityID?: string; NextExecutionDate?: string; ExcludeControlGroup?: boolean }): Observable<ActivationItemApi> {
    const endpoint = this.composeEndpoint('/webapp/audience/activations/add');
    return this.http.post<ApiResponse<ActivationItemApi>>(
      endpoint.url,
      payload,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        if (response.error || !response.payload) {
          throw new Error(response.error?.message || 'Unable to activate destination');
        }
        return response.payload;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to add activation item', error);
        return throwError(() => error);
      })
    );
  }

  editActivationItem(payload: { ID: string; AudienceID: string; DestinationID: number; ExternalSegmentID: string; NextExecutionDate?: string; ChannelData: Record<string, unknown>; ExcludeControlGroup?: boolean }): Observable<ActivationItemApi> {
    if (!payload?.ID || !payload.ExternalSegmentID) {
      return throwError(() => new Error('Missing activation identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/activations/edit');
    return this.http.post<ApiResponse<ActivationItemApi>>(
      endpoint.url,
      payload,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map((response) => {
        if (response.error || !response.payload) {
          throw new Error(response.error?.message || 'Unable to edit activation');
        }
        return response.payload;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to edit activation item', error);
        // Il rifiuto può avere un motivo che l'utente deve leggere (es. TTD che nega la modifica
        // del ParentElementId): si propaga il messaggio dell'API invece della stringa generata da Angular.
        return throwError(() => toApiError(error));
      })
    );
  }

  updateActivationStatus(payload: { AudienceActivationID: string; status: string; endDate?: string | null; permanentDelete?: boolean }): Observable<void> {
    if (!payload?.AudienceActivationID || !payload.status) {
      return throwError(() => new Error('Missing activation identifier or status'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/activations/update-status');
    const body = {
      status: payload.status,
      endDate: payload.endDate ?? null
    };
    let params = new HttpParams().set('AudienceActivationID', payload.AudienceActivationID);
    if (payload.permanentDelete) {
      params = params.set('permanentDelete', 'true');
    }
    return this.http.put<ApiResponse<string>>(
      endpoint.url,
      body,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false,
        params
      }
    ).pipe(
      map(response => this.ensureOkResponse(response, 'Unable to update activation status')),
      catchError(error => {
        console.error('[audience-api] Failed to update activation status', error);
        return throwError(() => error);
      })
    );
  }

  pushActivation(payload: { AudienceActivationID: string; AudienceName: string }): Observable<void> {
    if (!payload?.AudienceActivationID) {
      return throwError(() => new Error('Missing activation identifier'));
    }
    const endpoint = this.composeEndpoint('/webapp/audience/activations/push');
    const params = new HttpParams()
      .set('AudienceActivationID', payload.AudienceActivationID)
      .set('AudienceName', payload.AudienceName ?? '');
    return this.http.get<ApiResponse<{ status: string; message?: string }>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false,
        params
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to force push activation');
        }
        if (response.payload?.status === 'ERROR') {
          throw new Error(response.payload.message || 'Unable to force push activation');
        }
      }),
      catchError(error => {
        console.error('[audience-api] Failed to force push activation', error);
        return throwError(() => toApiError(error));
      })
    );
  }

  getCountries(): Observable<CountryOption[]> {
    const endpoint = this.composeEndpoint('/webapp/dashboard/generic/get-country-list');
    return this.http.get<ApiResponse<Array<{ CountryCode: string; CountryName: string }>>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load countries');
        }
        const payload = response.payload ?? [];
        return payload
          .map(item => ({
            code: item?.CountryCode || '',
            name: item?.CountryName || ''
          }))
          .filter(option => option.name);
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load countries', error);
        return throwError(() => error);
      })
    );
  }

  getCountryRegions(countryCode: string): Observable<string[]> {
    if (!countryCode) {
      return throwError(() => new Error('Missing country code'));
    }
    const endpoint = this.composeEndpoint('/webapp/dashboard/generic/get-region-list');
    return this.http.get<ApiResponse<Array<{ Name?: string | null; RegionCode?: string | null }>>>(
      endpoint.url,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false,
        params: new HttpParams().set('countryCode', countryCode)
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to load regions');
        }
        const payload = response.payload ?? [];
        const unique = Array.from(new Set(
          payload
            .map(item => (item.Name || '').toString().trim())
            .filter(name => !!name)
        ));
        return unique;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to load regions', error);
        return throwError(() => error);
      })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean, options?: { skipDcr?: boolean }): HttpHeaders {
    if (useRemote) {
      const token = this.authService.getTncApiToken();
      let remoteHeaders = new HttpHeaders({'Content-Type': 'application/json'});
      if (token) {
        remoteHeaders = remoteHeaders.set('Authorization', token);
      }
      return remoteHeaders;
    }

    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }

  private ensureOkResponse(response: ApiResponse<string>, fallbackMessage: string): void {
    if (response.error) {
      throw new Error(response.error.message || fallbackMessage);
    }
    if (!response.payload || response.payload.toUpperCase() !== 'OK') {
      throw new Error(fallbackMessage);
    }
  }

  private mapIntersection(item: any): AudienceIntersection {
    return {
      sourceAudienceId: item?.SourceAudienceID ?? '',
      targetAudienceId: item?.TargetAudienceID ?? '',
      tncIds: Number(item?.TNCIDs ?? 0),
      sourceOverlap: Number(item?.SourceAudienceOverlap ?? 0),
      targetOverlap: Number(item?.TargetAudienceOverlap ?? 0),
      sourceVolumes: Number(item?.SourceVolumes ?? 0),
      targetVolumes: Number(item?.TargetVolumes ?? 0),
      targetAudienceName: item?.TargetAudienceName ?? item?.TargetAudienceID ?? '—',
      sourceAudienceName: item?.SourceAudienceName ?? item?.SourceAudienceID ?? '—',
      targetAudienceDescription: item?.TargetAudienceDescription ?? '',
      targetEntityId: item?.TargetEntityID ?? null,
      targetIsPublic: item?.TargetIsPublic ?? null,
      sourceEntityId: item?.SourceEntityID ?? null
    };
  }

  activateDestination(payload: { AudienceID: string; DestinationIDList: number[]; ExternalSegmentID: string; EntityID?: string; ChannelData?: Record<string, unknown> }): Observable<void> {
    const endpoint = this.composeEndpoint('/webapp/audience/activations/add-group');
    return this.http.post<ApiResponse<string>>(
      endpoint.url,
      payload,
      {
        headers: this.buildHeaders(endpoint.useRemote),
        withCredentials: false
      }
    ).pipe(
      map(response => {
        if (response.error) {
          throw new Error(response.error.message || 'Unable to activate destination');
        }
        if (response.payload && response.payload !== 'OK') {
          throw new Error('Unable to activate destination');
        }
        return;
      }),
      catchError(error => {
        console.error('[audience-api] Failed to activate destination', error);
        return throwError(() => error);
      })
    );
  }
}
